const DEFAULT_THUMBNAIL_WIDTH = 180
const MAX_PIXEL_RATIO = 2
const MAX_CONCURRENT_RENDERS = 2

export default class PdfPageOverview {
  constructor({ dialog, grid, pdfDocument, pageLabel, onSelect, onError = console.error }) {
    this.dialog = dialog
    this.grid = grid
    this.pdfDocument = pdfDocument
    this.pageLabel = pageLabel
    this.onSelect = onSelect
    this.onError = onError
    this.queue = []
    this.activeRenders = 0
    this.renderTasks = new Set()
    this.objectUrls = new Set()
    this.disconnected = false
    this.handleClick = this.handleClick.bind(this)
    this.restoreFocus = this.restoreFocus.bind(this)
    this.grid.addEventListener("click", this.handleClick)
    this.dialog.addEventListener("close", this.restoreFocus)
  }

  open({ currentPage, opener }) {
    if (this.disconnected) return

    this.opener = opener
    this.build()
    this.markCurrentPage(currentPage)
    if (!this.dialog.open) this.dialog.showModal()

    requestAnimationFrame(() => {
      this.buttonForPage(currentPage)?.scrollIntoView({ block: "center", inline: "nearest" })
    })
  }

  close() {
    if (this.dialog.open) this.dialog.close()
  }

  disconnect() {
    this.disconnected = true
    this.observer?.disconnect()
    this.queue = []
    this.renderTasks.forEach((task) => task.cancel())
    this.renderTasks.clear()
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url))
    this.objectUrls.clear()
    this.grid.removeEventListener("click", this.handleClick)
    this.dialog.removeEventListener("close", this.restoreFocus)
    if (this.dialog.open) this.dialog.close()
  }

  build() {
    if (this.built) return

    const fragment = document.createDocumentFragment()
    for (let pageNumber = 1; pageNumber <= this.pdfDocument.numPages; pageNumber += 1) {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "pdf-page-overview__page"
      button.dataset.pageNumber = String(pageNumber)
      button.setAttribute("aria-label", this.labelFor(pageNumber))

      const preview = document.createElement("span")
      preview.className = "pdf-page-overview__preview"
      preview.setAttribute("aria-hidden", "true")

      button.append(preview)
      fragment.append(button)
    }
    this.grid.append(fragment)

    this.observer = new IntersectionObserver((entries) => this.observe(entries), {
      root: this.grid,
      rootMargin: "240px 0px"
    })
    this.pageButtons().forEach((button) => this.observer.observe(button))
    this.built = true
  }

  observe(entries) {
    entries.forEach((entry) => {
      const state = entry.target.dataset.thumbnailState
      if (!entry.isIntersecting || state) return

      entry.target.dataset.thumbnailState = "queued"
      this.queue.push(entry.target)
    })
    this.renderQueuedThumbnails()
  }

  renderQueuedThumbnails() {
    while (!this.disconnected && this.activeRenders < MAX_CONCURRENT_RENDERS && this.queue.length > 0) {
      const button = this.queue.shift()
      this.activeRenders += 1
      this.renderThumbnail(button).finally(() => {
        this.activeRenders -= 1
        this.renderQueuedThumbnails()
      })
    }
  }

  async renderThumbnail(button) {
    const pageNumber = Number(button.dataset.pageNumber)
    let canvas

    try {
      button.dataset.thumbnailState = "rendering"
      const pdfPage = await this.pdfDocument.getPage(pageNumber)
      if (this.disconnected) return

      const initialViewport = pdfPage.getViewport({ scale: 1 })
      const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO)
      const scale = DEFAULT_THUMBNAIL_WIDTH * pixelRatio / initialViewport.width
      const viewport = pdfPage.getViewport({ scale })
      canvas = document.createElement("canvas")
      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)

      const renderTask = pdfPage.render({
        canvasContext: canvas.getContext("2d", { alpha: false }),
        viewport
      })
      this.renderTasks.add(renderTask)
      try {
        await renderTask.promise
      } finally {
        this.renderTasks.delete(renderTask)
      }
      if (this.disconnected) return

      const blob = await this.canvasBlob(canvas)
      if (this.disconnected) return

      const url = URL.createObjectURL(blob)
      this.objectUrls.add(url)
      const image = document.createElement("img")
      image.src = url
      image.alt = ""
      image.width = DEFAULT_THUMBNAIL_WIDTH
      image.height = Math.round(DEFAULT_THUMBNAIL_WIDTH * viewport.height / viewport.width)
      button.querySelector(".pdf-page-overview__preview").replaceChildren(image)
      button.dataset.thumbnailState = "rendered"
      this.observer.unobserve(button)
    } catch (error) {
      if (this.disconnected || error?.name === "RenderingCancelledException") return

      button.dataset.thumbnailState = "error"
      button.classList.add("is-error")
      this.observer.unobserve(button)
      this.onError(error)
    } finally {
      // The image owns the compressed result; release the temporary bitmap.
      if (canvas) {
        canvas.width = 0
        canvas.height = 0
      }
    }
  }

  canvasBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob)
        else reject(new Error("Unable to create PDF thumbnail"))
      }, "image/webp", 0.8)
    })
  }

  markCurrentPage(pageNumber) {
    this.pageButtons().forEach((button) => {
      const current = Number(button.dataset.pageNumber) === pageNumber
      button.classList.toggle("is-current", current)
      if (current) button.setAttribute("aria-current", "page")
      else button.removeAttribute("aria-current")
    })
  }

  handleClick(event) {
    const button = event.target.closest?.("[data-page-number]")
    if (!button || !this.grid.contains(button)) return

    const pageNumber = Number(button.dataset.pageNumber)
    this.close()
    this.onSelect(pageNumber)
  }

  restoreFocus() {
    this.opener?.focus()
  }

  pageButtons() {
    return Array.from(this.grid.querySelectorAll("[data-page-number]"))
  }

  buttonForPage(pageNumber) {
    return this.grid.querySelector(`[data-page-number="${pageNumber}"]`)
  }

  labelFor(pageNumber) {
    return this.pageLabel.replace("%{page}", pageNumber)
  }
}
